import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Window20SpreadDistribution() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/spread-distribution`); setData(res.data); }
      catch { toast.error('Failed to fetch spread analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 20</h1><p className="text-xs text-gray-500">Spread & Distribution Analysis</p></div>
            </div>
          </div>

            <ExportButton data={data?.last_20} filename="w20_spread_distribution" />
          </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W20" {...NARRATIVES.W20} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <Tabs defaultValue="spread" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1">
              <TabsTrigger value="spread" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-spread">Spread</TabsTrigger>
              <TabsTrigger value="quartiles" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-quartiles">Quartile Patterns</TabsTrigger>
              <TabsTrigger value="gaps" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-gaps">Gap Distribution</TabsTrigger>
              <TabsTrigger value="spread-gaps" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-spread-gaps">Spread Gap Analysis</TabsTrigger>
              <TabsTrigger value="recent" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-recent">Last 20 Draws</TabsTrigger>
              <TabsTrigger value="ls-spread" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white font-bold" data-testid="tab-ls-spread">Lucky Star Spread</TabsTrigger>
            </TabsList>

            <TabsContent value="spread">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Avg Spread</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="stat-avg-spread">{data.avg_spread}</p>
                </div>
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Min / Max Spread</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{data.min_spread} / {data.max_spread}</p>
                </div>
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Total Draws</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{data.total_draws}</p>
                </div>
              </div>

              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-4">Spread Distribution (High - Low)</h3>
                <div className="space-y-1" data-testid="spread-distribution">
                  {data.spread_distribution.map(r => {
                    const maxCount = Math.max(...data.spread_distribution.map(x => x.count));
                    const gapInfo = data.bucket_gap_stats?.find(b => b.label === r.range);
                    const bucket = parseInt(r.range.split('-')[0]);
                    const isPeak = bucket >= 30 && bucket <= 40;
                    return (
                      <div key={r.range} className={`flex items-center gap-2 ${isPeak ? 'bg-teal-50 rounded px-1' : ''}`}>
                        <span className={`font-mono text-xs w-14 text-right font-bold ${isPeak ? 'text-teal-700' : ''}`}>{r.range}</span>
                        <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden relative">
                          <div className={`h-full rounded transition-all ${isPeak ? 'bg-teal-600' : 'bg-[#002FA7]'}`} style={{ width: `${(r.count / maxCount) * 100}%` }} />
                          <span className="absolute right-2 top-0 text-[10px] font-mono font-bold leading-5">{r.count}x</span>
                        </div>
                        <span className="font-mono text-xs w-12 text-right font-bold">{r.pct}%</span>
                        {gapInfo && (
                          <>
                            <span className="font-mono text-[10px] w-20 text-center text-gray-500">avg <span className="font-bold text-gray-700">{gapInfo.avg_gap}</span></span>
                            <span className="font-mono text-[10px] w-20 text-center text-gray-500">max <span className="font-bold text-gray-800">{gapInfo.max_gap}</span></span>
                            <span className={`font-mono text-[10px] w-16 text-center font-bold ${gapInfo.gap_ratio >= 1.5 ? 'text-red-600' : gapInfo.gap_ratio >= 1 ? 'text-amber-600' : 'text-green-600'}`}>{gapInfo.gap_ratio}x</span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-6 text-white">
                <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Most Probable Spread Range</h3></div>
                <p className="text-xs text-white/70 mb-4">Historical average spread: {data.avg_spread} (distance between highest and lowest number)</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[...data.spread_distribution].sort((a, b) => b.count - a.count).slice(0, 5).map((r, idx) => (
                    <div key={r.range} className={`p-3 rounded-lg ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`}>
                      <div className="text-xl font-mono font-bold text-center">{r.range}</div>
                      <div className="text-xs text-center mt-1">{r.count}x ({r.pct}%)</div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="quartiles">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-1">Quartile Distribution Patterns</h3>
                <p className="text-xs text-gray-500 mb-3">Format: Q1(1-12) - Q2(13-25) - Q3(26-38) - Q4(39-50) — how many balls in each quartile</p>
                <div className="space-y-2" data-testid="quartile-patterns">
                  {data.quartile_patterns.map((p, idx) => {
                    const maxCount = data.quartile_patterns[0]?.count || 1;
                    return (
                      <div key={p.pattern} className="flex items-center gap-2">
                        <span className={`font-mono text-sm w-24 text-right font-bold ${idx === 0 ? 'text-[#002FA7]' : ''}`}>{p.pattern}</span>
                        <div className="flex-1 bg-gray-100 rounded h-6 overflow-hidden relative">
                          <div className={`h-full rounded transition-all ${idx === 0 ? 'bg-[#002FA7]' : 'bg-blue-400'}`} style={{ width: `${(p.count / maxCount) * 100}%` }} />
                          <span className="absolute right-2 top-0.5 text-[10px] font-mono font-bold">{p.count}x</span>
                        </div>
                        <span className="font-mono text-xs w-12 text-right font-bold">{p.pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="gaps">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-1">Gap Between Consecutive Sorted Numbers</h3>
                <p className="text-xs text-gray-500 mb-3">Distribution of gaps between adjacent sorted ball values in each draw</p>
                <div className="space-y-1" data-testid="gap-distribution">
                  {data.gap_distribution.map(g => {
                    const maxCount = Math.max(...data.gap_distribution.map(x => x.count));
                    return (
                      <div key={g.gap} className="flex items-center gap-2">
                        <span className="font-mono text-xs w-10 text-right font-bold">{g.gap}</span>
                        <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden relative">
                          <div className={`h-full rounded transition-all ${g.gap <= 5 ? 'bg-green-500' : g.gap <= 15 ? 'bg-blue-500' : 'bg-red-500'}`} style={{ width: `${(g.count / maxCount) * 100}%` }} />
                          <span className="absolute right-2 top-0 text-[10px] font-mono font-bold leading-5">{g.count}x</span>
                        </div>
                        <span className="font-mono text-xs w-12 text-right">{g.pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="spread-gaps">
              {data.bucket_gap_stats && (() => {
                const sorted = [...data.bucket_gap_stats].sort((a, b) => a.bucket - b.bucket);
                const topPred = [...data.bucket_gap_stats].sort((a, b) => b.pred_score - a.pred_score).slice(0, 3);
                const maxGapOverall = Math.max(...sorted.map(b => b.max_gap));
                return (
                  <>
                    {/* Top 3 Prediction */}
                    <div className="bg-gradient-to-r from-teal-700 to-cyan-800 rounded-lg p-5 mb-6 text-white" data-testid="spread-gap-prediction">
                      <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Spread Prediction — Next Draw</h3></div>
                      <p className="text-xs text-white/70 mb-4">Top 3 most-likely spread buckets based on historical frequency + overdue gap ratio</p>
                      <div className="grid grid-cols-3 gap-4">
                        {topPred.map((b, idx) => {
                          const medal = idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : 'text-amber-600';
                          return (
                            <div key={b.bucket} className={`rounded-lg p-4 ${idx === 0 ? 'bg-white/20 ring-1 ring-white/40' : 'bg-white/10'}`} data-testid={`spread-pred-${idx}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className={`font-bold text-sm ${medal}`}>#{idx + 1}</span>
                                <span className="font-mono font-bold text-2xl text-white">{b.label}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                <div className="text-white/60">Frequency:</div>
                                <div className="font-bold text-right">{b.pct}% ({b.count}x)</div>
                                <div className="text-white/60">Current Gap:</div>
                                <div className={`font-bold text-right ${b.current_gap > b.avg_gap ? 'text-red-300' : 'text-white'}`}>{b.current_gap} draws</div>
                                <div className="text-white/60">Avg Gap:</div>
                                <div className="font-bold text-right">{b.avg_gap} draws</div>
                                <div className="text-white/60">Max Gap:</div>
                                <div className="font-bold text-right">{b.max_gap} draws</div>
                                <div className="text-white/60">Expected:</div>
                                <div className="font-bold text-right">{b.expected_gap} draws</div>
                                <div className="text-white/60">Due Ratio:</div>
                                <div className={`font-bold text-right ${b.gap_ratio >= 1.5 ? 'text-red-300' : b.gap_ratio >= 1 ? 'text-amber-300' : 'text-green-300'}`}>{b.gap_ratio}x</div>
                              </div>
                              <div className="mt-2 pt-2 border-t border-white/20 flex items-center justify-between">
                                <span className="text-[10px] text-white/50">Pred Score</span>
                                <span className={`font-mono font-bold text-xl ${idx === 0 ? 'text-yellow-300' : 'text-white'}`}>{b.pred_score}</span>
                              </div>
                              {b.is_peak && <div className="text-[9px] text-teal-300 font-bold text-right">PEAK RANGE</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Full Table */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-1">Spread Bucket Gap Analysis</h3>
                      <p className="text-xs text-gray-500 mb-3">Per-bucket: historical count, current gap since last appearance, average gap between appearances, maximum gap ever, expected gap, and overdue ratio. Peak range (30-44) highlighted.</p>
                      <div className="overflow-x-auto" data-testid="spread-gap-table">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-[10px] uppercase">Spread</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Count</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Freq %</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Current Gap</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Avg Gap</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Max Gap</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Expected</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Due Ratio</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Pred Score</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Max Gap Bar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sorted.map((b, idx) => (
                              <tr key={b.bucket}
                                className={b.is_peak ? '' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                style={b.is_peak ? {borderLeft: '4px solid #0d9488', backgroundColor: 'rgba(13, 148, 136, 0.06)'} : b.is_extended ? {borderLeft: '3px solid #06b6d4', backgroundColor: 'rgba(6, 182, 212, 0.03)'} : {}}
                                data-testid={`sgap-row-${b.bucket}`}>
                                <td className="px-3 py-2 border-b">
                                  <span className={`font-mono font-bold text-sm ${b.is_peak ? 'text-teal-700' : b.is_extended ? 'text-cyan-700' : 'text-gray-600'}`}>{b.label}</span>
                                  {b.is_peak && <span className="ml-1 text-[8px] bg-teal-100 text-teal-700 px-1 rounded font-bold">PEAK</span>}
                                  {b.is_extended && !b.is_peak && <span className="ml-1 text-[8px] bg-cyan-100 text-cyan-600 px-1 rounded">EXT</span>}
                                </td>
                                <td className="px-3 py-2 border-b font-mono text-center font-bold">{b.count}</td>
                                <td className="px-3 py-2 border-b font-mono text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <div className="w-16 bg-gray-100 rounded h-3 overflow-hidden">
                                      <div className={`h-full rounded ${b.is_peak ? 'bg-teal-500' : b.is_extended ? 'bg-cyan-400' : 'bg-gray-300'}`} style={{width: `${Math.min(b.pct / 25 * 100, 100)}%`}} />
                                    </div>
                                    <span className="font-bold">{b.pct}%</span>
                                  </div>
                                </td>
                                <td className={`px-3 py-2 border-b font-mono text-center font-bold ${b.current_gap > b.avg_gap ? 'text-red-600' : 'text-gray-700'}`}>{b.current_gap}</td>
                                <td className="px-3 py-2 border-b font-mono text-center text-gray-600">{b.avg_gap}</td>
                                <td className="px-3 py-2 border-b font-mono text-center font-bold text-gray-800">{b.max_gap}</td>
                                <td className="px-3 py-2 border-b font-mono text-center text-gray-500">{b.expected_gap}</td>
                                <td className="px-3 py-2 border-b text-center">
                                  <span className={`font-mono font-bold px-2 py-0.5 rounded text-sm ${b.gap_ratio >= 1.5 ? 'bg-red-100 text-red-700' : b.gap_ratio >= 1 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{b.gap_ratio}x</span>
                                </td>
                                <td className={`px-3 py-2 border-b font-mono text-center font-bold text-lg ${b.pred_score >= 20 ? 'text-amber-600' : b.pred_score >= 15 ? 'text-teal-700' : 'text-gray-500'}`}>{b.pred_score}</td>
                                <td className="px-3 py-2 border-b">
                                  <div className="flex items-center gap-1">
                                    <div className="w-24 bg-gray-100 rounded h-4 overflow-hidden">
                                      <div className={`h-full rounded ${b.max_gap === maxGapOverall ? 'bg-red-500' : 'bg-blue-400'}`} style={{width: `${(b.max_gap / maxGapOverall) * 100}%`}} />
                                    </div>
                                    <span className="font-mono text-[10px]">{b.max_gap}</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </TabsContent>

            <TabsContent value="recent">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-3">Last 20 Draws — Spread & Coverage</h3>
                <div className="overflow-x-auto" data-testid="recent-table">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center">Draw</th>
                        <th className="px-3 py-2 text-center">Date</th>
                        <th className="px-3 py-2 text-center">Balls</th>
                        <th className="px-3 py-2 text-center">Spread</th>
                        <th className="px-3 py-2 text-center">Avg Gap</th>
                        <th className="px-3 py-2 text-center">Max Gap</th>
                        <th className="px-3 py-2 text-center">Quartile</th>
                        <th className="px-3 py-2 text-center">Decades</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.last_20.map((d, idx) => (
                        <tr key={d.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-1.5 border-b font-mono text-center">#{d.draw}</td>
                          <td className="px-3 py-1.5 border-b text-center">{d.date}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center">{d.balls.join(', ')}</td>
                          <td className={`px-3 py-1.5 border-b font-mono text-center font-bold ${d.spread > data.avg_spread + 5 ? 'text-green-700' : d.spread < data.avg_spread - 5 ? 'text-red-600' : ''}`}>{d.spread}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center">{d.avg_gap}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center font-bold">{d.max_gap}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center">{d.quartile}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center">{d.decades_covered}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* ========= LUCKY STAR SPREAD TAB ========= */}
            <TabsContent value="ls-spread">
              {data.ls_spread && (() => {
                const ls = data.ls_spread;
                const topPred = [...ls.bucket_stats].sort((a, b) => b.pred_score - a.pred_score).slice(0, 3);
                const maxCount = Math.max(...ls.distribution.map(x => x.count));
                const maxGapOverall = Math.max(...ls.bucket_stats.map(b => b.max_gap));
                return (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white border rounded p-4">
                        <p className="text-xs uppercase font-bold text-gray-500">Avg LS Spread</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="ls-avg-spread">{ls.avg_spread}</p>
                      </div>
                      <div className="bg-white border rounded p-4">
                        <p className="text-xs uppercase font-bold text-gray-500">Min / Max LS Spread</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{ls.min_spread} / {ls.max_spread}</p>
                      </div>
                      <div className="bg-white border rounded p-4">
                        <p className="text-xs uppercase font-bold text-gray-500">Total Draws</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{data.total_draws}</p>
                      </div>
                    </div>

                    {/* LS Spread Distribution Chart */}
                    <div className="bg-white border rounded p-4 mb-6">
                      <h3 className="font-bold text-gray-900 mb-4">Lucky Star Spread Distribution (LS2 - LS1)</h3>
                      <div className="space-y-1" data-testid="ls-spread-distribution">
                        {ls.distribution.map(r => {
                          const gapInfo = ls.bucket_stats.find(b => b.spread === r.spread);
                          return (
                            <div key={r.spread} className="flex items-center gap-2">
                              <span className="font-mono text-xs w-8 text-right font-bold">{r.spread}</span>
                              <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden relative">
                                <div className="h-full bg-amber-500 rounded transition-all" style={{ width: `${(r.count / maxCount) * 100}%` }} />
                                <span className="absolute right-2 top-0 text-[10px] font-mono font-bold leading-5">{r.count}x</span>
                              </div>
                              <span className="font-mono text-xs w-12 text-right font-bold">{r.pct}%</span>
                              {gapInfo && (
                                <>
                                  <span className="font-mono text-[10px] w-20 text-center text-gray-500">avg <span className="font-bold text-gray-700">{gapInfo.avg_gap}</span></span>
                                  <span className="font-mono text-[10px] w-20 text-center text-gray-500">max <span className="font-bold text-gray-800">{gapInfo.max_gap}</span></span>
                                  <span className={`font-mono text-[10px] w-16 text-center font-bold ${gapInfo.gap_ratio >= 1.5 ? 'text-red-600' : gapInfo.gap_ratio >= 1 ? 'text-amber-600' : 'text-green-600'}`}>{gapInfo.gap_ratio}x</span>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Top 3 Prediction */}
                    <div className="bg-gradient-to-r from-amber-700 to-orange-800 rounded-lg p-5 mb-6 text-white" data-testid="ls-spread-prediction">
                      <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">LS Spread Prediction — Next Draw</h3></div>
                      <p className="text-xs text-white/70 mb-4">Top 3 most-likely Lucky Star spread values based on frequency + overdue gap ratio</p>
                      <div className="grid grid-cols-3 gap-4">
                        {topPred.map((b, idx) => {
                          const medal = idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : 'text-amber-500';
                          return (
                            <div key={b.spread} className={`rounded-lg p-4 ${idx === 0 ? 'bg-white/20 ring-1 ring-white/40' : 'bg-white/10'}`} data-testid={`ls-pred-${idx}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className={`font-bold text-sm ${medal}`}>#{idx + 1}</span>
                                <span className="font-mono font-bold text-3xl text-white">{b.spread}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                <div className="text-white/60">Frequency:</div>
                                <div className="font-bold text-right">{b.pct}% ({b.count}x)</div>
                                <div className="text-white/60">Current Gap:</div>
                                <div className={`font-bold text-right ${b.current_gap > b.avg_gap ? 'text-red-300' : 'text-white'}`}>{b.current_gap} draws</div>
                                <div className="text-white/60">Avg Gap:</div>
                                <div className="font-bold text-right">{b.avg_gap} draws</div>
                                <div className="text-white/60">Max Gap:</div>
                                <div className="font-bold text-right">{b.max_gap} draws</div>
                                <div className="text-white/60">Expected:</div>
                                <div className="font-bold text-right">{b.expected_gap} draws</div>
                                <div className="text-white/60">Due Ratio:</div>
                                <div className={`font-bold text-right ${b.gap_ratio >= 1.5 ? 'text-red-300' : b.gap_ratio >= 1 ? 'text-amber-300' : 'text-green-300'}`}>{b.gap_ratio}x</div>
                              </div>
                              <div className="mt-2 pt-2 border-t border-white/20 flex items-center justify-between">
                                <span className="text-[10px] text-white/50">Pred Score</span>
                                <span className={`font-mono font-bold text-xl ${idx === 0 ? 'text-yellow-300' : 'text-white'}`}>{b.pred_score}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Full Table */}
                    <div className="bg-white border rounded p-4 mb-6">
                      <h3 className="font-bold text-gray-900 mb-1">Lucky Star Spread Gap Analysis</h3>
                      <p className="text-xs text-gray-500 mb-3">Per-spread: count, frequency, current gap, avg gap, max gap, expected gap, due ratio, and prediction score.</p>
                      <div className="overflow-x-auto" data-testid="ls-gap-table">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Spread</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Count</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Freq %</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Current Gap</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Avg Gap</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Max Gap</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Expected</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Due Ratio</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Pred Score</th>
                              <th className="px-3 py-2 text-center text-[10px] uppercase">Max Gap Bar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...ls.bucket_stats].sort((a, b) => a.spread - b.spread).map((b, idx) => (
                              <tr key={b.spread}
                                className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                style={b.gap_ratio >= 1.5 ? {borderLeft: '4px solid #dc2626', backgroundColor: 'rgba(220, 38, 38, 0.06)'} : b.gap_ratio >= 1 ? {borderLeft: '4px solid #f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.04)'} : {}}
                                data-testid={`ls-gap-row-${b.spread}`}>
                                <td className="px-3 py-2 border-b font-mono text-center font-bold text-lg">{b.spread}</td>
                                <td className="px-3 py-2 border-b font-mono text-center font-bold">{b.count}</td>
                                <td className="px-3 py-2 border-b font-mono text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <div className="w-16 bg-gray-100 rounded h-3 overflow-hidden">
                                      <div className="h-full rounded bg-amber-500" style={{width: `${Math.min(b.pct / 20 * 100, 100)}%`}} />
                                    </div>
                                    <span className="font-bold">{b.pct}%</span>
                                  </div>
                                </td>
                                <td className={`px-3 py-2 border-b font-mono text-center font-bold ${b.current_gap > b.avg_gap ? 'text-red-600' : 'text-gray-700'}`}>{b.current_gap}</td>
                                <td className="px-3 py-2 border-b font-mono text-center text-gray-600">{b.avg_gap}</td>
                                <td className="px-3 py-2 border-b font-mono text-center font-bold text-gray-800">{b.max_gap}</td>
                                <td className="px-3 py-2 border-b font-mono text-center text-gray-500">{b.expected_gap}</td>
                                <td className="px-3 py-2 border-b text-center">
                                  <span className={`font-mono font-bold px-2 py-0.5 rounded text-sm ${b.gap_ratio >= 1.5 ? 'bg-red-100 text-red-700' : b.gap_ratio >= 1 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{b.gap_ratio}x</span>
                                </td>
                                <td className={`px-3 py-2 border-b font-mono text-center font-bold text-lg ${b.pred_score >= 20 ? 'text-red-600' : b.pred_score >= 10 ? 'text-amber-600' : 'text-gray-500'}`}>{b.pred_score}</td>
                                <td className="px-3 py-2 border-b">
                                  <div className="flex items-center gap-1">
                                    <div className="w-24 bg-gray-100 rounded h-4 overflow-hidden">
                                      <div className={`h-full rounded ${b.max_gap === maxGapOverall ? 'bg-red-500' : 'bg-amber-400'}`} style={{width: `${(b.max_gap / maxGapOverall) * 100}%`}} />
                                    </div>
                                    <span className="font-mono text-[10px]">{b.max_gap}</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Last 20 LS Draws */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-3">Last 20 Draws — Lucky Star Spread</h3>
                      <div className="overflow-x-auto" data-testid="ls-recent-table">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-center">Draw</th>
                              <th className="px-3 py-2 text-center">Date</th>
                              <th className="px-3 py-2 text-center">Lucky Stars</th>
                              <th className="px-3 py-2 text-center">Spread</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ls.last_20.map((d, idx) => (
                              <tr key={d.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-3 py-1.5 border-b font-mono text-center">#{d.draw}</td>
                                <td className="px-3 py-1.5 border-b text-center">{d.date}</td>
                                <td className="px-3 py-1.5 border-b font-mono text-center font-bold">{d.ls.join(', ')}</td>
                                <td className={`px-3 py-1.5 border-b font-mono text-center font-bold ${d.spread > ls.avg_spread + 2 ? 'text-green-700' : d.spread < ls.avg_spread - 2 ? 'text-red-600' : ''}`}>{d.spread}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </TabsContent>
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}
